import React, { useState } from "react";
import { ShieldCheck, Loader2, Copy, Check, Smartphone } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Enrollment flow for TOTP two-factor. Used both in the mandatory admin gate
// and in Settings. On success it refreshes the session so mfa_enabled flips.
export const MfaSetup = ({ onDone, forced }) => {
  const { refresh } = useAuth();
  const [step, setStep] = useState("intro"); // intro | scan | codes
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState([]);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const begin = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setQr(data.qr); setSecret(data.secret); setStep("scan");
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/mfa/enable", { code });
      setBackup(data.backup_codes || []); setStep("codes");
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const finish = async () => { try { await refresh?.(); } catch {} onDone?.(); };

  return (
    <div data-testid="mfa-setup">
      {step === "intro" && (
        <div>
          <div className="flex items-center gap-2 mb-3"><ShieldCheck size={20} className="text-[#14A89A]" /><h3 className="font-heading text-xl text-white">Set up two-factor authentication</h3></div>
          <p className="font-body text-sm text-zinc-400 mb-3 leading-relaxed">Add a second step to your sign-in using an authenticator app on your phone, so a stolen password alone can't get in.</p>
          <div className="border border-[#14A89A]/25 bg-[#14A89A]/[0.05] p-3 mb-4">
            <p className="font-body text-xs text-zinc-300 leading-relaxed"><span className="text-[#14A89A] font-medium">We recommend Ente Auth or Proton Authenticator</span>, both open source, not owned by Big Tech, work on iOS / Android / desktop, and keep their optional backups end-to-end encrypted. Any authenticator app works (Aegis, Google Authenticator, etc.), these are just our suggestions, never required.</p>
          </div>
          {error && <p className="text-red-400 text-sm mb-3 font-body" data-testid="mfa-setup-error">{error}</p>}
          <button onClick={begin} disabled={busy} data-testid="mfa-begin-button" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-5 py-2.5 font-body text-sm disabled:opacity-40 flex items-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : <Smartphone size={15} />} Begin setup</button>
        </div>
      )}

      {step === "scan" && (
        <div>
          <h3 className="font-heading text-xl text-white mb-2">Scan this code</h3>
          <p className="font-body text-sm text-zinc-400 mb-4">Scan with your authenticator app, then enter the 6-digit code it shows.</p>
          {qr && <img src={qr} alt="Authenticator QR code" data-testid="mfa-qr" className="w-44 h-44 bg-white p-2 rounded mb-3" />}
          <p className="font-body text-[11px] text-zinc-500 mb-1">Can't scan? Enter this key manually:</p>
          <button onClick={() => { navigator.clipboard.writeText(secret); setCopied(true); setTimeout(() => setCopied(false), 1500); }} className="font-mono text-xs text-[#14A89A] break-all mb-4 flex items-center gap-1.5" data-testid="mfa-secret">{secret} {copied ? <Check size={12} /> : <Copy size={12} />}</button>
          <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder="123456" data-testid="mfa-confirm-input" className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2.5 text-white font-body outline-none focus:border-[#14A89A] mb-3" />
          {error && <p className="text-red-400 text-sm mb-3 font-body" data-testid="mfa-setup-error">{error}</p>}
          <button onClick={confirm} disabled={busy || !code} data-testid="mfa-confirm-button" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-5 py-2.5 font-body text-sm disabled:opacity-40 flex items-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Verify &amp; turn on</button>
        </div>
      )}

      {step === "codes" && (
        <div>
          <div className="flex items-center gap-2 mb-2"><ShieldCheck size={20} className="text-[#14A89A]" /><h3 className="font-heading text-xl text-white">Two-factor is on, save your backup codes</h3></div>
          <p className="font-body text-sm text-zinc-400 mb-4 leading-relaxed">Store these somewhere safe and offline. Each works <span className="text-white">once</span> if you ever lose your authenticator. We can't recover them for you, this is self-custody, just like your recovery phrase.</p>
          <div className="grid grid-cols-2 gap-2 mb-3" data-testid="mfa-backup-codes">
            {backup.map((c, i) => <div key={i} className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-center font-mono text-sm text-[#14A89A]">{c}</div>)}
          </div>
          <button onClick={() => { navigator.clipboard.writeText(backup.join("\n")); setCopied(true); setTimeout(() => setCopied(false), 1500); }} className="text-zinc-400 hover:text-white text-xs font-body mb-4 flex items-center gap-1.5">{copied ? <Check size={12} /> : <Copy size={12} />} Copy all</button>
          <label className="flex items-start gap-2 mb-4 cursor-pointer">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="mfa-codes-ack" className="mt-1 accent-[#14A89A]" />
            <span className="font-body text-xs text-zinc-300">I've saved my backup codes.</span>
          </label>
          <button onClick={finish} disabled={!ack} data-testid="mfa-done-button" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-5 py-2.5 font-body text-sm disabled:opacity-40">{forced ? "Continue to Sovereign Signal" : "Done"}</button>
        </div>
      )}
    </div>
  );
};

// Compact Security section for Settings/Edit Profile: enable, disable, or
// regenerate backup codes. Disabling / regenerating requires a current code.
export const MfaSettings = () => {
  const { user, refresh } = useAuth();
  const [enrolling, setEnrolling] = useState(false);
  const [mode, setMode] = useState(null); // "disable" | "regen"
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [newCodes, setNewCodes] = useState(null);
  const on = !!user?.mfa_enabled;

  const act = async () => {
    setBusy(true); setError("");
    try {
      if (mode === "disable") {
        await api.post("/auth/mfa/disable", { code });
        try { await refresh?.(); } catch {}
        setMode(null); setCode("");
      } else {
        const { data } = await api.post("/auth/mfa/backup-codes", { code });
        setNewCodes(data.backup_codes || []); setMode(null); setCode("");
      }
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (enrolling) return (
    <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="mfa-settings">
      <MfaSetup onDone={() => setEnrolling(false)} />
    </div>
  );

  return (
    <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="mfa-settings">
      <div className="flex items-center gap-2 mb-1"><ShieldCheck size={16} className={on ? "text-[#14A89A]" : "text-zinc-500"} /><span className="font-body text-sm text-white">Two-step sign-in (2FA)</span>{on && <span className="text-[10px] text-[#14A89A] uppercase tracking-wider ml-1">On</span>}{user?.mfa_enforced && <span className="text-[10px] text-amber-400/80 ml-auto">Required for your role</span>}</div>
      <p className="font-body text-xs text-zinc-500 mb-3 leading-relaxed">An authenticator-app code at sign-in, so a stolen password alone can't get in.</p>
      {newCodes && (
        <div className="mb-3" data-testid="mfa-new-backup-codes">
          <p className="font-body text-xs text-zinc-300 mb-1.5">Your new backup codes (each works once):</p>
          <div className="grid grid-cols-2 gap-2">{newCodes.map((c, i) => <div key={i} className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-center font-mono text-sm text-[#14A89A]">{c}</div>)}</div>
        </div>
      )}
      {!on && <button onClick={() => setEnrolling(true)} data-testid="mfa-enable-link" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-2 font-body text-xs">Set up two-factor</button>}
      {on && !mode && (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setMode("regen"); setError(""); }} data-testid="mfa-regen-link" className="border border-white/15 text-zinc-300 px-3 py-2 font-body text-xs hover:bg-white/5">New backup codes</button>
          {!user?.mfa_enforced && <button onClick={() => { setMode("disable"); setError(""); }} data-testid="mfa-disable-link" className="border border-white/15 text-zinc-400 px-3 py-2 font-body text-xs hover:bg-white/5">Turn off</button>}
        </div>
      )}
      {on && mode && (
        <div>
          <p className="font-body text-xs text-zinc-400 mb-2">{mode === "disable" ? "Enter a current code to turn off two-factor." : "Enter a current code to generate new backup codes."}</p>
          <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="123456 or backup code" data-testid="mfa-action-code" className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 text-white font-body outline-none focus:border-[#14A89A] mb-2" />
          {error && <p className="text-red-400 text-xs mb-2 font-body" data-testid="mfa-action-error">{error}</p>}
          <div className="flex gap-2">
            <button onClick={act} disabled={busy || !code} data-testid="mfa-action-confirm" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-2 font-body text-xs disabled:opacity-40">{busy ? "…" : "Confirm"}</button>
            <button onClick={() => { setMode(null); setCode(""); setError(""); }} className="text-zinc-500 font-body text-xs px-2">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
